import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment-timezone';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableFooter from '@material-ui/core/TableFooter';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import PayIcon from '@material-ui/icons/PaymentSharp';
import { useEffect } from 'react';

function createData(
  o: any,
  _restId: number,
  _alcoholDeducted: boolean,
  _billDeducted: boolean
) {
  return {
    dateTime: `${o?.bill_date?.substring(0, 5)} / ${moment(
      o.bill_time,
      'hh:mm:ss a'
    ).format('HH:mm')}`,
    billId: o.bill_id,
    beerAmt: o.beer_amount,
    liquorAmt: o.liquor_amount,
    foodAmt: o.taxable_amount,
    billTotal: o.bill_total,
    taxAmt: o.total_tax_amount,
    discAmt: o.discount_amount,
    grandTotal: o.grand_total,
    _restId: _restId,
    _alcoholDeducted: _alcoholDeducted,
    _billDeducted: _billDeducted,
  };
}

function descendingComparator(a: any, b: any, orderBy: any) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order: string, orderBy: any) {
  return order === 'desc'
    ? (a: any, b: any) => descendingComparator(a, b, orderBy)
    : (a: any, b: any) => -descendingComparator(a, b, orderBy);
}

function stableSort(array: any[], comparator: any) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'dateTime',
    numeric: false,
    disablePadding: true,
    label: 'Date /Time',
  },
  { id: 'billId', numeric: false, disablePadding: true, label: 'Bill No' },
  {
    id: 'beerAmt',
    numeric: true,
    disablePadding: false,
    label: 'Beer',
  },
  {
    id: 'liquorAmt',
    numeric: true,
    disablePadding: false,
    label: 'Liquor',
  },
  { id: 'foodAmt', numeric: true, disablePadding: false, label: 'Food' },
  { id: 'billTotal', numeric: true, disablePadding: false, label: 'Total' },
  { id: 'taxAmt', numeric: true, disablePadding: false, label: 'GST (5%)' },
  { id: 'discAmt', numeric: true, disablePadding: false, label: 'Disc' },
  { id: 'grandTotal', numeric: true, disablePadding: false, label: 'G. Total' },
];

function EnhancedTableHead(props: any) {
  const {
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    paymentDisabled,
  } = props;
  const createSortHandler = (property: any) => (event: any) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <RedCheckbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ 'aria-label': 'select all bills' }}
            disabled={paymentDisabled}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  paymentDisabled: PropTypes.bool.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
          // color: theme.palette.secondary.main,
          // backgroundColor: lighten(theme.palette.secondary.light, 0.85),
          color: '#FFFFFF',
          backgroundColor: '#c23616',
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: '1 1 100%',
  },
}));

const EnhancedTableToolbar = (props: any) => {
  const classes = useToolbarStyles();
  const { selected, onPayHandler, paymentDisabled } = props;
  let numSelected = selected.length;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      {numSelected > 0 ? (
        <Typography
          className={classes.title}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          className={classes.title}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Food Bills
        </Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Pay">
          <IconButton aria-label="pay" onClick={(_) => onPayHandler(selected)}>
            <PayIcon style={{ color: paymentDisabled ? 'grey' : 'white' }} />
          </IconButton>
        </Tooltip>
      ) : (
        ''
      )}
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  selected: PropTypes.array.isRequired,
  onPayHandler: PropTypes.func.isRequired,
  paymentDisabled: PropTypes.bool.isRequired,
};

const RedCheckbox = withStyles({
  root: {
    '&$checked': {
      color: '#c23616',
    },
  },
  checked: {},
})((props: { [x: string]: any }) => <Checkbox color="default" {...props} />);

const CustomTableCell = withStyles({
  body: {
    fontSize: 16,
    fontWeight: 800,
  },
  footer: {
    fontSize: 16,
    fontWeight: 800,
    color: 'black',
  },
})((props: { [x: string]: any }) => <TableCell {...props} />);

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  footer: {
    backgroundColor: 'f1c40f',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

interface EnhancedTableProps {
  data: any[];
  onPayHandler: (e: any) => void;
  paymentDisabled: boolean;
}

const EnhancedTable = ({
  data,
  onPayHandler,
  paymentDisabled,
}: EnhancedTableProps) => {
  // Filter bills only in display when an invoice does not have any food bills.
  // This is done in order to fix the UI and not distrub the reports for empty food bills.
  const filteredData = data.filter(
    (item) =>
      (item?.bill_date === undefined &&
        item?.bill_id &&
        item?.bill_id.length > 0) ||
      (item?.bill_date &&
        item?.bill_date.length > 0 &&
        item?.bill_id === undefined)
  );

  const totals = {
    beerAmt: 0,
    liquorAmt: 0,
    foodAmt: 0,
    totalAmt: 0,
    taxAmt: 0,
    discAmt: 0,
    grandTotal: 0,
  };
  const deductions = {
    beerAmt: 0,
    liquorAmt: 0,
    foodAmt: 0,
    totalAmt: 0,
    taxAmt: 0,
    discAmt: 0,
    grandTotal: 0,
  };

  filteredData.forEach((o) => {
    let info = o?.bill_info;
    let roundedTax = Math.round(info?.sgst_amount || 0) * 2;
    totals.beerAmt += info?.beer_amount || 0;
    totals.liquorAmt += info?.liquor_amount || 0;
    totals.foodAmt += info?.taxable_amount || 0;
    totals.totalAmt += info?.bill_total || 0;
    totals.taxAmt += roundedTax;
    totals.discAmt += info?.discount_amount || 0;
    totals.grandTotal +=
      info?.beer_amount +
      info?.liquor_amount +
      info?.taxable_amount +
      roundedTax;

    if (o?.bill_deducted) {
      deductions.beerAmt += info?.beer_amount || 0;
      deductions.liquorAmt += info?.liquor_amount || 0;
      deductions.foodAmt += info?.taxable_amount || 0;
      deductions.totalAmt += info?.bill_total || 0;
      deductions.taxAmt += roundedTax;
      deductions.discAmt += info?.discount_amount || 0;
      deductions.grandTotal +=
        info?.beer_amount +
        info?.liquor_amount +
        info?.taxable_amount +
        roundedTax;
    } else if (o?.alcohol_deducted) {
      deductions.beerAmt += info?.beer_amount || 0;
      deductions.liquorAmt += info?.liquor_amount || 0;
      deductions.totalAmt +=
        (info?.beer_amount || 0) + (info?.liquor_amount || 0);
      deductions.discAmt +=
        (info?.discount_amount || 0) -
        ((info?.pre_discount_food_total || 0) - (info?.taxable_amount || 0));
      deductions.grandTotal +=
        (info?.beer_amount || 0) + (info?.liquor_amount || 0);
    }
  });

  const rows = filteredData.map((item) =>
    createData(
      item.bill_info || {},
      item.restaurant_transaction_id || -1,
      item.alcohol_deducted === true,
      item.bill_deducted === true
    )
  );

  const classes = useStyles();
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('dateTime');
  const [selected, setSelected] = React.useState<any[]>(new Array<any>());

  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(true);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  useEffect(() => {
    setSelected(new Array<any>());
  }, [data]);

  const handleRequestSort = (event: any, property: any) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: any) => {
    if (event.target.checked) {
      const newSelecteds = rows
        .filter((r) => !(r._alcoholDeducted || r._billDeducted))
        .map((n) => ({
          billId: n.billId,
          restId: n._restId,
        }));
      setSelected(newSelecteds);
    } else {
      setSelected([]);
    }
  };

  const handleClick = (_: any, billId: string, restId: number) => {
    const selectedIndex = selected.findIndex(
      (val) => val.billId === billId && val.restId === restId
    );
    let newSelected = new Array<any>();

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, {
        billId: billId,
        restId: restId,
      });
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (_: any, newPage: any) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event: any) => {
    setDense(event.target.checked);
  };

  const isSelected = (billId: any) =>
    selected.findIndex((val) => val.billId === billId) !== -1;

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <EnhancedTableToolbar
          selected={selected}
          onPayHandler={onPayHandler}
          paymentDisabled={paymentDisabled}
        />
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              paymentDisabled={paymentDisabled}
            />
            <TableBody>
              {stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.billId);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  let alcoholBgColor =
                    row._alcoholDeducted || row._billDeducted
                      ? '#d35400'
                      : 'default';

                  let billBgColor = row._billDeducted ? '#d35400' : 'default';

                  return (
                    <TableRow
                      hover
                      onClick={(event) =>
                        paymentDisabled ||
                        row._alcoholDeducted ||
                        row._billDeducted
                          ? ''
                          : handleClick(event, row.billId, row._restId)
                      }
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.billId}
                      selected={isItemSelected}
                    >
                      <CustomTableCell
                        padding="checkbox"
                        style={{ backgroundColor: billBgColor }}
                      >
                        <RedCheckbox
                          checked={isItemSelected}
                          inputProps={{ 'aria-labelledby': labelId }}
                          disabled={
                            paymentDisabled ||
                            row._alcoholDeducted ||
                            row._billDeducted
                          }
                        />
                      </CustomTableCell>
                      <CustomTableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                        style={{
                          backgroundColor: billBgColor,
                          display: 'default',
                        }}
                      >
                        {row.dateTime}
                      </CustomTableCell>
                      <CustomTableCell
                        padding="none"
                        style={{ backgroundColor: billBgColor }}
                      >
                        {row.billId}
                      </CustomTableCell>
                      <CustomTableCell
                        align="right"
                        style={{ backgroundColor: alcoholBgColor }}
                      >
                        {row.beerAmt}
                      </CustomTableCell>
                      <CustomTableCell
                        align="right"
                        style={{ backgroundColor: alcoholBgColor }}
                      >
                        {row.liquorAmt}
                      </CustomTableCell>
                      <CustomTableCell
                        align="right"
                        style={{ backgroundColor: billBgColor }}
                      >
                        {row.foodAmt}
                      </CustomTableCell>
                      <CustomTableCell
                        align="right"
                        style={{ backgroundColor: billBgColor }}
                      >
                        {row.billTotal}
                      </CustomTableCell>
                      <CustomTableCell
                        align="right"
                        style={{ backgroundColor: billBgColor }}
                      >
                        {Math.round(row.taxAmt * 100) / 100}
                      </CustomTableCell>
                      <CustomTableCell
                        align="right"
                        style={{ backgroundColor: billBgColor }}
                        size="small"
                      >
                        {row.discAmt}
                      </CustomTableCell>
                      <CustomTableCell
                        align="right"
                        style={{ backgroundColor: billBgColor }}
                      >
                        {row.grandTotal}
                      </CustomTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={12}>
                  <hr style={{ marginTop: '12px' }} />
                </TableCell>
              </TableRow>
              <TableRow style={{ backgroundColor: 'rgba(241, 196, 15, 0.4)' }}>
                <CustomTableCell colSpan={3} align="center">
                  TOTALS
                </CustomTableCell>
                <CustomTableCell align="right">
                  {totals.beerAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {totals.liquorAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {totals.foodAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {totals.totalAmt}
                </CustomTableCell>
                <CustomTableCell align="right">{totals.taxAmt}</CustomTableCell>
                <CustomTableCell align="right">
                  {totals.discAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {totals.grandTotal}
                </CustomTableCell>
              </TableRow>
              <TableRow style={{ backgroundColor: 'rgba(211, 84, 0, 0.4)' }}>
                <CustomTableCell colSpan={3} align="center">
                  DEDUCTIONS
                </CustomTableCell>
                <CustomTableCell align="right">
                  {deductions.beerAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {deductions.liquorAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {deductions.foodAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {deductions.totalAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {deductions.taxAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {deductions.discAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {deductions.grandTotal}
                </CustomTableCell>
              </TableRow>
              <TableRow style={{ backgroundColor: 'rgba(39, 174, 96, 0.6)' }}>
                <CustomTableCell colSpan={3} align="center">
                  GRAND TOTALS
                </CustomTableCell>
                <CustomTableCell align="right">
                  {totals.beerAmt - deductions.beerAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {totals.liquorAmt - deductions.liquorAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {totals.foodAmt - deductions.foodAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {totals.totalAmt - deductions.totalAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {totals.taxAmt - deductions.taxAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {totals.discAmt - deductions.discAmt}
                </CustomTableCell>
                <CustomTableCell align="right">
                  {totals.grandTotal - deductions.grandTotal}
                </CustomTableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label="Dense padding"
      />
    </div>
  );
};

export default EnhancedTable;
